import type { NonFsdsFeedbackQuote } from '@/components/homepage/non-fsds-sections/non-fsds-feedback-section'

import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'

export interface LandingInstructor {
  id: number
  username: string
  position: string
  description: string
  avatar: string
  transform: string
  linkedin: string
  course: string
  module: string
  status?: string
}

export interface NonFsdsFeatureItem {
  title: string
  subtitle: string
  bullets: string[]
  image: string
  isComingSoon?: boolean
}

export interface NonFsdsLocalizedContent {
  heroTitle: string
  heroDescription: string
  ctaStart: string
  ctaContinue: string
  ctaExplore: string
  ctaStartUrl: string
  ctaContinueUrl: string
  ctaExploreUrl: string
  trustItems: string[]
  stats: Array<{ label: string; value: string }>
  partnersTitle: string
  guaranteeTitle: string
  guaranteeDescription: string
  projectTitle: string
  projectDescription: string
  featurePoweredTitle: string
  featurePoweredDescription: string
  featurePoweredItems: NonFsdsFeatureItem[]
  feedbackTitle: string
  feedbackQuotes: (string | NonFsdsFeedbackQuote)[]
  faqTitle: string
  faqs: Array<{ id: string; q: string; a: string }>
  expertInstructorsTitle: string
}

export const NON_FSDS_CONTENT: Record<'en' | 'vi', NonFsdsLocalizedContent> = {
  en: {
    heroTitle: 'Build practical AI and data skills for your team',
    heroDescription:
      'A blue-themed learning experience for brands with the same proven journey: roadmap, projects, mentors, and outcomes.',
    ctaStart: 'Start Learning',
    ctaContinue: 'Continue Learning',
    ctaExplore: 'Explore curriculum',
    ctaStartUrl: TEMPLATE_DEFAULT_URLS.myCourses,
    ctaContinueUrl: TEMPLATE_DEFAULT_URLS.learningPath,
    ctaExploreUrl: TEMPLATE_DEFAULT_URLS.learningPath,
    trustItems: ['Live mentorship', 'Project-first curriculum', 'Career-focused outcomes'],
    stats: [
      { label: 'Learners', value: '20k+' },
      { label: 'Projects built', value: '150+' },
      { label: 'Mentors', value: '35+' },
      { label: 'Completion rate', value: '89%' },
    ],
    partnersTitle: 'Trusted by teams from',
    guaranteeTitle: '30-day money-back guarantee',
    guaranteeDescription: 'Try the program with confidence. If it is not a fit, get a full refund in 30 days.',
    projectTitle: 'Portfolio-ready projects',
    projectDescription: 'Hands-on, review-based delivery with clear evaluation criteria.',
    featurePoweredTitle: 'Powered learning stack',
    featurePoweredDescription: 'Mentor office hours, project rubrics, and progress analytics in one workflow.',
    featurePoweredItems: [
      {
        title: 'Efficient Review System',
        subtitle: 'Watch, practice, submit — all in one place',
        bullets: [
          'Watch live-session recordings directly on the platform',
          'Complete quizzes, code homework, and submit final projects online',
        ],
        image: '/images/home/feature-powered/quiz.png',
      },
      {
        title: 'AI Teaching Assistant',
        subtitle: 'AI-powered learning support',
        bullets: [
          'Ask questions answered by LLM analysis',
          'Automated code review with logic & performance improvement suggestions',
        ],
        image: '/images/home/feature-powered/ai-assistant.png',
        isComingSoon: true,
      },
      {
        title: 'Hands-on Sandbox',
        subtitle: 'Deploy industry-ready projects instantly',
        bullets: [
          'Work on pre-configured environments — no setup required',
          'Experiment and debug directly in the browser',
        ],
        image: '/images/home/feature-powered/sandbox.png',
      },
      {
        title: 'AI Research & Career Agent',
        subtitle: 'Stay ahead with AI-curated insights',
        bullets: [
          'Track the latest Data & AI research and reports',
          'Get personalized career opportunities and job recommendations',
        ],
        image: '/images/home/feature-powered/ai-research.png',
        isComingSoon: true,
      },
    ],
    feedbackTitle: 'What learners say',
    feedbackQuotes: [
      'The weekly mentor feedback helped me fix real project issues fast.',
      'I stopped learning in isolation and started improving with a real peer network.',
      'The roadmap was clear, practical, and directly useful for my job transition.',
      'Each milestone felt achievable because the coaching support was consistent.',
    ],
    faqTitle: 'Frequently asked questions',
    faqs: [
      {
        id: 'faq-1',
        q: 'How is this different from self-paced video courses?',
        a: 'You get mentor guidance, structured milestones, and project reviews instead of only passive content.',
      },
      {
        id: 'faq-2',
        q: 'Can I learn while working full-time?',
        a: 'Yes. Weekly workload is planned for working professionals with clear priorities and checkpoints.',
      },
      {
        id: 'faq-3',
        q: 'Do I need strong coding background?',
        a: 'Basic programming helps, but the path is scaffolded from fundamentals to production-oriented projects.',
      },
    ],
    expertInstructorsTitle: 'Expert instructors',
  },
  vi: {
    heroTitle: 'Xây dựng năng lực AI và dữ liệu thực chiến cho đội ngũ',
    heroDescription:
      'Trang đích xanh cho ứng dụng non-FSDS, giữ nguyên hành trình học hiệu quả: lộ trình, dự án, mentor và kết quả.',
    ctaStart: 'Bắt đầu học',
    ctaContinue: 'Tiếp tục học',
    ctaExplore: 'Xem lộ trình',
    ctaStartUrl: TEMPLATE_DEFAULT_URLS.myCourses,
    ctaContinueUrl: TEMPLATE_DEFAULT_URLS.learningPath,
    ctaExploreUrl: TEMPLATE_DEFAULT_URLS.learningPath,
    trustItems: ['Mentor đồng hành', 'Học qua dự án', 'Định hướng nghề nghiệp'],
    stats: [
      { label: 'Học viên', value: '20k+' },
      { label: 'Dự án', value: '150+' },
      { label: 'Mentor', value: '35+' },
      { label: 'Tỷ lệ hoàn thành', value: '89%' },
    ],
    partnersTitle: 'Được tin dùng bởi các đội ngũ từ',
    guaranteeTitle: 'Hoàn tiền trong 30 ngày',
    guaranteeDescription: 'Bạn có thể học thử và nhận hoàn tiền đầy đủ trong 30 ngày nếu chương trình chưa phù hợp.',
    projectTitle: 'Dự án thực chiến cho portfolio',
    projectDescription: 'Bài tập theo tình huống thực tế, có phản hồi và tiêu chí đánh giá rõ ràng.',
    featurePoweredTitle: 'Nền tảng học tập đồng bộ',
    featurePoweredDescription: 'Office hours cùng mentor, rubric chấm dự án và theo dõi tiến độ trong một quy trình.',
    featurePoweredItems: [
      {
        title: 'Hệ thống ôn tập hiệu quả',
        subtitle: 'Xem lại, luyện tập, nộp bài — tất cả trên một nền tảng',
        bullets: ['Xem lại recording trực tiếp trên platform', 'Làm quiz, code homework, và nộp final project online'],
        image: '/images/home/feature-powered/quiz.png',
      },
      {
        title: 'AI Teaching Assistant',
        subtitle: 'Hỗ trợ học tập bằng AI',
        bullets: [
          'Phân tích và trả lời câu hỏi bằng LLM',
          'Review & chấm code tự động, gợi ý cải thiện logic và hiệu năng',
        ],
        image: '/images/home/feature-powered/ai-assistant.png',
        isComingSoon: true,
      },
      {
        title: 'Sandbox thực chiến',
        subtitle: 'Triển khai dự án industry-ready ngay lập tức',
        bullets: [
          'Triển khai dự án trên môi trường dựng sẵn',
          'Không cần cài đặt, có thể thử nghiệm và debug trực tiếp',
        ],
        image: '/images/home/feature-powered/sandbox.png',
      },
      {
        title: 'AI Research & Career Agent',
        subtitle: 'Cập nhật xu hướng cùng AI',
        bullets: [
          'Theo dõi và cập nhật các nghiên cứu, báo cáo mới nhất về Data & AI',
          'Gợi ý cơ hội nghề nghiệp, tin tuyển dụng phù hợp',
        ],
        image: '/images/home/feature-powered/ai-research.png',
        isComingSoon: true,
      },
    ],
    feedbackTitle: 'Học viên nói gì',
    feedbackQuotes: [
      'Phản hồi hàng tuần từ mentor giúp mình sửa lỗi dự án nhanh hơn nhiều.',
      'Mình không còn học một mình vì luôn có cộng đồng cùng trao đổi.',
      'Lộ trình rõ ràng, thực tế và áp dụng được ngay vào công việc.',
      'Mỗi cột mốc đều khả thi vì có hỗ trợ đều đặn từ đội ngũ coaching.',
    ],
    faqTitle: 'Câu hỏi thường gặp',
    faqs: [
      {
        id: 'faq-1',
        q: 'Khóa học này khác gì so với video tự học?',
        a: 'Bạn được mentor đồng hành, có cột mốc rõ ràng và review dự án thay vì chỉ xem nội dung thụ động.',
      },
      {
        id: 'faq-2',
        q: 'Có thể học khi đang đi làm toàn thời gian không?',
        a: 'Có. Khối lượng học mỗi tuần được thiết kế cho người đi làm với ưu tiên và checkpoint rõ ràng.',
      },
      {
        id: 'faq-3',
        q: 'Cần nền tảng code mạnh không?',
        a: 'Biết lập trình cơ bản là lợi thế, lộ trình vẫn có hướng dẫn từ nền tảng đến dự án thực chiến.',
      },
    ],
    expertInstructorsTitle: 'Đội ngũ giảng viên',
  },
}

export const NON_FSDS_PROJECTS = [
  'Customer churn prediction',
  'Sales forecasting dashboard',
  'Recommendation engine prototype',
]

export const NON_FSDS_PARTNERS = ['Tech startup', 'E-commerce', 'EdTech', 'Fintech', 'Enterprise']

export const NON_FSDS_VI_FEATURES = ['Lộ trình rõ ràng', 'Mentor phản hồi nhanh', 'Bài tập bám thực tế']
